import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Local storage key for guest cart
const GUEST_CART_KEY = 'kitstop_guest_cart';

export const CartProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);

  // Load guest cart from localStorage
  const loadGuestCart = useCallback(() => {
    try {
      const savedCart = localStorage.getItem(GUEST_CART_KEY);
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        const total = parsed.items.reduce((sum, item) => sum + (item.product_price * item.quantity), 0);
        setCart({
          items: parsed.items || [],
          total: total,
          item_count: parsed.items?.length || 0
        });
      }
    } catch (error) {
      console.error('Failed to load guest cart:', error);
    }
  }, []);

  // Save guest cart to localStorage
  const saveGuestCart = useCallback((items) => {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify({ items }));
    } catch (error) {
      console.error('Failed to save guest cart:', error);
    }
  }, []);

  // Load cart from API (for authenticated users)
  const loadCart = useCallback(async () => {
    if (!isAuthenticated) {
      loadGuestCart();
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCart(response.data);
      // Clear guest cart after loading user cart
      localStorage.removeItem(GUEST_CART_KEY);
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadGuestCart]);

  // Sync guest cart to server when user logs in
  const syncGuestCartToServer = useCallback(async () => {
    try {
      const savedCart = localStorage.getItem(GUEST_CART_KEY);
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (parsed.items && parsed.items.length > 0) {
          // Add each guest cart item to the server cart
          for (const item of parsed.items) {
            try {
              await api.post('/cart/add', { 
                product_id: item.product_id, 
                quantity: item.quantity 
              });
            } catch (e) {
              console.error('Failed to sync item:', e);
            }
          }
          // Clear guest cart after syncing
          localStorage.removeItem(GUEST_CART_KEY);
          // Reload cart from server
          const response = await api.get('/cart');
          setCart(response.data);
          toast.success('Carrinho sincronizado!');
        }
      }
    } catch (error) {
      console.error('Failed to sync guest cart:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      syncGuestCartToServer().then(() => loadCart());
    } else {
      loadGuestCart();
    }
  }, [isAuthenticated, loadCart, loadGuestCart, syncGuestCartToServer]);

  const addToCart = async (productId, quantity = 1, productData = null) => {
    // Guest cart (not logged in)
    if (!isAuthenticated) {
      const existingItem = cart.items.find(item => item.product_id === productId);
      let newItems;
      
      if (existingItem) {
        newItems = cart.items.map(item => 
          item.product_id === productId 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Need product data for guest cart
        if (!productData) {
          try {
            const response = await api.get(`/products/${productId}`);
            productData = response.data;
          } catch (error) {
            toast.error('Erro ao adicionar ao carrinho');
            return false;
          }
        }
        
        newItems = [...cart.items, {
          product_id: productId,
          quantity: quantity,
          product_name: productData.name,
          product_price: productData.price,
          product_image: productData.images?.[0] || null
        }];
      }
      
      const total = newItems.reduce((sum, item) => sum + (item.product_price * item.quantity), 0);
      setCart({
        items: newItems,
        total: total,
        item_count: newItems.length
      });
      saveGuestCart(newItems);
      toast.success('Adicionado ao carrinho!');
      return true;
    }
    
    // Logged in user - use API
    try {
      await api.post('/cart/add', { product_id: productId, quantity });
      await loadCart();
      toast.success('Adicionado ao carrinho!');
      return true;
    } catch (error) {
      toast.error('Erro ao adicionar ao carrinho');
      return false;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    // Guest cart
    if (!isAuthenticated) {
      let newItems;
      if (quantity <= 0) {
        newItems = cart.items.filter(item => item.product_id !== productId);
      } else {
        newItems = cart.items.map(item =>
          item.product_id === productId
            ? { ...item, quantity: quantity }
            : item
        );
      }
      
      const total = newItems.reduce((sum, item) => sum + (item.product_price * item.quantity), 0);
      setCart({
        items: newItems,
        total: total,
        item_count: newItems.length
      });
      saveGuestCart(newItems);
      return;
    }
    
    // Logged in user
    try {
      await api.put('/cart/update', { product_id: productId, quantity });
      await loadCart();
    } catch (error) {
      toast.error('Erro ao atualizar quantidade');
    }
  };

  const removeFromCart = async (productId) => {
    // Guest cart
    if (!isAuthenticated) {
      const newItems = cart.items.filter(item => item.product_id !== productId);
      const total = newItems.reduce((sum, item) => sum + (item.product_price * item.quantity), 0);
      setCart({
        items: newItems,
        total: total,
        item_count: newItems.length
      });
      saveGuestCart(newItems);
      toast.success('Item removido');
      return;
    }
    
    // Logged in user
    try {
      await api.delete(`/cart/remove/${productId}`);
      await loadCart();
      toast.success('Item removido');
    } catch (error) {
      toast.error('Erro ao remover item');
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0, item_count: 0 });
      localStorage.removeItem(GUEST_CART_KEY);
      return;
    }
    
    try {
      await api.delete('/cart/clear');
      setCart({ items: [], total: 0, item_count: 0 });
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart: loadCart,
    itemCount: cart.items.length,
    total: cart.total
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
